import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useInView, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { EASE_OUT } from "../../kit.jsx";

export const EASE_IN_OUT = [0.65, 0, 0.35, 1];

export const clamp01 = (value) => Math.min(1, Math.max(0, value));

/* Counter that accepts a prefix and suffix ("₹", "+", " lakh"). Reduced motion shows the final value. */
export function Count({ to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const progress = useMotionValue(0);
  const text = useTransform(progress, (latest) => `${prefix}${Math.round(latest).toLocaleString("en-IN")}${suffix}`);
  const final = `${prefix}${to.toLocaleString("en-IN")}${suffix}`;

  useEffect(() => {
    if (!inView || reduceMotion) return undefined;
    const controls = animate(progress, to, { duration: 1.3, ease: EASE_OUT });
    return () => controls.stop();
  }, [inView, progress, reduceMotion, to]);

  if (reduceMotion) return <span className="pg-count" ref={ref}>{final}</span>;
  return (
    <span className="pg-count" ref={ref}>
      <span className="sr-only">{final}</span>
      <motion.span aria-hidden="true">{text}</motion.span>
    </span>
  );
}

export function Btn({ to, href, children, variant = "fill", icon = null }) {
  const label = typeof children === "string" ? children : undefined;
  const inner = (
    <>
      <span className="roll" data-label={label}><span>{children}</span></span>
      {icon}
    </>
  );
  const className = `gh-btn gh-btn--${variant} pg-btn`;
  if (to) return <Link className={className} to={to}>{inner}</Link>;
  const external = href?.startsWith("http");
  return (
    <a className={className} href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
      {inner}
    </a>
  );
}

export function GoLink({ to, href, children, className = "" }) {
  if (to) {
    return (
      <Link className={`gh-link ${className}`} to={to}>
        {children} <ArrowRight weight="bold" aria-hidden="true" />
      </Link>
    );
  }
  const external = href?.startsWith("http") || href?.endsWith(".pdf");
  return (
    <a className={`gh-link ${className}`} href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
      {children} {external ? <ArrowUpRight weight="bold" aria-hidden="true" /> : <ArrowRight weight="bold" aria-hidden="true" />}
    </a>
  );
}

/* Fade-rise used for short intro copy. Visible immediately when reduced motion is on. */
export function Rise({ as = "div", children, className = "", delay = 0, onMount = false, ...rest }) {
  const reduceMotion = useReducedMotion();
  const Tag = motion[as];
  const trigger = onMount
    ? { animate: { opacity: 1, y: 0 } }
    : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.3 } };
  return (
    <Tag
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      {...trigger}
      transition={{ duration: 0.9, delay, ease: EASE_OUT }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
